import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Phone, Mail, Calendar, FileText, CreditCard,
    Edit, Activity, User, MapPin, Shield,
} from 'lucide-react';
import { api, Patient, Appointment, SoapNote } from '../lib/api';

interface Invoice {
    id: string;
    invoiceNumber: string;
    status: string;
    totalCents: string;
    dueDate: string;
    paidAt: string | null;
    paymentMethod: string | null;
}

const invoiceStatusLabels: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Borrador', cls: 'badge-neutral' },
    sent: { label: 'Enviada', cls: 'badge-primary' },
    paid: { label: 'Pagada', cls: 'badge-success' },
    overdue: { label: 'Vencida', cls: 'badge-danger' },
    cancelled: { label: 'Cancelada', cls: 'badge-danger' },
};

export function PatientDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [patient, setPatient] = useState<(Patient & { appointments: Appointment[]; soapNotes: SoapNote[] }) | null>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<'overview' | 'appointments' | 'notes' | 'billing'>('overview');
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);

    useEffect(() => {
        if (!id) return;
        loadPatient();
    }, [id]);

    async function loadPatient() {
        setLoading(true);
        try {
            const res = await api.patients.get(id!);
            setPatient(res.data);
        } catch {
            console.warn('Patient API unavailable for', id);
            setPatient(null);
        } finally {
            setLoading(false);
        }
    }

    async function loadInvoices() {
        if (!id) return;
        setInvoicesLoading(true);
        try {
            const res = await fetch(`/api/billing/invoices?patientId=${id}&limit=20`);
            const json = await res.json();
            setInvoices(json.data || []);
        } catch {
            console.warn('Billing API unavailable');
        } finally {
            setInvoicesLoading(false);
        }
    }

    useEffect(() => {
        if (tab === 'billing' && invoices.length === 0) loadInvoices();
    }, [tab]);

    if (loading) {
        return (
            <div className="page-content">
                <div className="skeleton" style={{ height: 200, marginBottom: 24 }} />
                <div className="skeleton" style={{ height: 400 }} />
            </div>
        );
    }

    if (!patient) {
        return <div className="page-content"><p>Paciente no encontrado</p></div>;
    }

    const age = Math.floor((Date.now() - new Date(patient.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000));

    return (
        <div className="page-content fade-in">
            {/* Back Button */}
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/patients')}
                style={{ marginBottom: 'var(--space-4)' }}>
                <ArrowLeft size={16} /> Volver a pacientes
            </button>

            {/* Patient Header Card */}
            <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center' }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: 'var(--radius-full)',
                            background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-light))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 'var(--text-2xl)', fontWeight: 700,
                        }}>
                            {patient.firstName[0]}{patient.lastName[0]}
                        </div>
                        <div>
                            <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
                                {patient.firstName} {patient.lastName}
                            </h1>
                            <div style={{ display: 'flex', gap: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                                <span className="badge badge-primary">{patient.clinicPatientId}</span>
                                <span>{age} años • {patient.gender === 'female' ? 'Femenino' : patient.gender === 'male' ? 'Masculino' : 'Otro'}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <Calendar size={12} /> {new Date(patient.dateOfBirth).toLocaleDateString('es-CO')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button className="btn btn-secondary btn-sm"><Edit size={14} /> Editar</button>
                </div>

                {/* Contact Info */}
                <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                        <Phone size={14} color="var(--color-text-muted)" /> {patient.mobilePhone}
                    </span>
                    {patient.email && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                            <Mail size={14} color="var(--color-text-muted)" /> {patient.email}
                        </span>
                    )}
                    {patient.cedula && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                            <Shield size={14} color="var(--color-text-muted)" /> Cédula: {patient.cedula}
                        </span>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 2, marginBottom: 'var(--space-6)', borderBottom: '2px solid var(--color-border)' }}>
                {([
                    { key: 'overview', label: 'Resumen', icon: User },
                    { key: 'appointments', label: 'Citas', icon: Calendar },
                    { key: 'notes', label: 'Notas SOAP', icon: FileText },
                    { key: 'billing', label: 'Facturación', icon: CreditCard },
                ] as const).map(({ key, label, icon: Icon }) => (
                    <button key={key} onClick={() => setTab(key)}
                        style={{
                            padding: 'var(--space-3) var(--space-5)',
                            fontSize: 'var(--text-sm)', fontWeight: 500,
                            color: tab === key ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                            borderBottom: tab === key ? '2px solid var(--color-primary)' : '2px solid transparent',
                            marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6,
                            transition: 'all var(--transition-fast)',
                        }}>
                        <Icon size={16} /> {label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {tab === 'overview' && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon stat-icon-primary"><Calendar size={24} /></div>
                        <div><div className="stat-value">{patient.appointments.length}</div><div className="stat-label">Citas totales</div></div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon stat-icon-success"><FileText size={24} /></div>
                        <div><div className="stat-value">{patient.soapNotes.length}</div><div className="stat-label">Notas SOAP</div></div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon stat-icon-warning"><Activity size={24} /></div>
                        <div><div className="stat-value">{patient.soapNotes.filter(n => n.status === 'signed').length}</div><div className="stat-label">Notas firmadas</div></div>
                    </div>
                </div>
            )}

            {tab === 'appointments' && (
                <div className="card" style={{ padding: 0 }}>
                    <div className="table-container" style={{ border: 'none' }}>
                        <table className="table">
                            <thead><tr><th>Fecha</th><th>Tipo</th><th>Estado</th><th>Motivo</th></tr></thead>
                            <tbody>
                                {patient.appointments.length === 0 ? (
                                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>Sin citas registradas</td></tr>
                                ) : patient.appointments.map((a) => (
                                    <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/appointments`)}>
                                        <td style={{ fontWeight: 500 }}>{new Date(a.scheduledStart).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                                        <td>{a.appointmentType === 'telehealth' ? 'Teleconsulta' : 'Presencial'}</td>
                                        <td><span className={`badge ${a.status === 'completed' ? 'badge-success' : a.status === 'cancelled' ? 'badge-danger' : 'badge-neutral'}`}>{a.status}</span></td>
                                        <td>{a.chiefComplaint ?? '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'notes' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {patient.soapNotes.length === 0 ? (
                        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>Sin notas SOAP</div>
                    ) : patient.soapNotes.map((n) => (
                        <div key={n.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/encounters/${n.id}`)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 600 }}>
                                        {n.chiefComplaint ?? 'Consulta'}
                                    </div>
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                        {new Date(n.createdAt).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
                                    </div>
                                </div>
                                <span className={`badge ${n.status === 'signed' ? 'badge-success' : n.status === 'draft' ? 'badge-warning' : 'badge-primary'}`}>
                                    {n.status === 'signed' ? 'Firmada' : n.status === 'draft' ? 'Borrador' : n.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === 'billing' && (
                <div className="card" style={{ padding: 0 }}>
                    <div className="table-container" style={{ border: 'none' }}>
                        <table className="table">
                            <thead><tr><th>Factura</th><th>Estado</th><th>Total</th><th>Vencimiento</th><th>Pagado</th></tr></thead>
                            <tbody>
                                {invoicesLoading ? (
                                    Array.from({ length: 3 }).map((_, i) => (
                                        <tr key={i}>{Array.from({ length: 5 }).map((_, j) => (
                                            <td key={j}><div className="skeleton" style={{ height: 16 }} /></td>
                                        ))}</tr>
                                    ))
                                ) : invoices.length === 0 ? (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>
                                        <CreditCard size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                                        <p>Sin facturas registradas</p>
                                    </td></tr>
                                ) : (
                                    invoices.map(inv => {
                                        const st = invoiceStatusLabels[inv.status] || { label: inv.status, cls: 'badge-neutral' };
                                        const total = (Number(inv.totalCents) / 100).toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 });
                                        return (
                                            <tr key={inv.id}>
                                                <td style={{ fontWeight: 600 }}>{inv.invoiceNumber}</td>
                                                <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                                                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{total}</td>
                                                <td style={{ fontSize: 'var(--text-sm)' }}>{new Date(inv.dueDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}</td>
                                                <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                                                    {inv.paidAt ? new Date(inv.paidAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
